import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import { alpha } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type FC, useMemo } from 'react';
import type { WorkingHours } from 'services/types/user';
import { NAVY, RADIUS_ITEM, YELLOW } from 'styles/tokens';

// JS getDay() convention: 0 = Sunday … 6 = Saturday. UI shows Mon → Sun.
const WEEK_DAYS: { idx: number; label: string }[] = [
  { idx: 1, label: 'Lun' },
  { idx: 2, label: 'Mar' },
  { idx: 3, label: 'Mié' },
  { idx: 4, label: 'Jue' },
  { idx: 5, label: 'Vie' },
  { idx: 6, label: 'Sáb' },
  { idx: 0, label: 'Dom' },
];

// Half-hour grid. Start picks [0, 23.5]; end picks [0.5, 24] so a day can run until midnight.
const START_OPTIONS = Array.from({ length: 48 }, (_, i) => i / 2);
const END_OPTIONS = Array.from({ length: 48 }, (_, i) => (i + 1) / 2);

const formatHour = (h: number): string => {
  const hh = Math.floor(h) % 24;
  const mm = h - Math.floor(h) === 0.5 ? '30' : '00';
  return `${hh.toString().padStart(2, '0')}:${mm}`;
};

const selectSx = {
  width: '100%',
  '&.MuiInputBase-root, &.MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiOutlinedInput-root': {
    borderRadius: RADIUS_ITEM,
    bgcolor: '#ffffff',
    boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.08)}`,
    transition: 'box-shadow 0.18s ease',
    fontFamily: 'Jost, sans-serif',
    fontSize: '0.9375rem',
    '& fieldset': { border: 'none' },
    '&:hover': { boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.18)}` },
    '&.Mui-focused': {
      boxShadow: `inset 0 0 0 1.5px ${NAVY}, 0 0 0 4px ${alpha(YELLOW, 0.25)}`,
    },
    '&.Mui-disabled': {
      boxShadow: `inset 0 0 0 1px ${alpha(NAVY, 0.06)}`,
      bgcolor: '#ffffff',
    },
  },
  '& .MuiSelect-select': { py: 1.25, px: 1.75, color: NAVY, minHeight: '24px' },
} as const;

/** Build a per-day map from the wire format. First matching range wins per day. */
const workingHoursToDayMap = (wh: WorkingHours | null | undefined): Record<number, { start: number; end: number }> => {
  const map: Record<number, { start: number; end: number }> = {};
  if (!wh) return map;
  wh.forEach((range) => {
    range.days.forEach((d) => {
      if (map[d] === undefined) {
        map[d] = { start: range.start, end: range.end };
      }
    });
  });
  return map;
};

/** Serialize the per-day map to the wire format (one entry per enabled day). */
const dayMapToWorkingHours = (map: Record<number, { start: number; end: number }>): WorkingHours => {
  return Object.entries(map)
    .filter(([, v]) => v.end > v.start)
    .map(([day, v]) => ({ start: v.start, end: v.end, days: [Number(day)] }));
};

interface WorkingHoursEditorProps {
  value: WorkingHours | null | undefined;
  onChange: (next: WorkingHours | null) => void;
}

const WorkingHoursEditor: FC<WorkingHoursEditorProps> = ({ value, onChange }) => {
  const dayMap = useMemo(() => workingHoursToDayMap(value), [value]);

  const updateDay = (dayIdx: number, next: { start: number; end: number } | null) => {
    const newMap = { ...dayMap };
    if (next === null) {
      delete newMap[dayIdx];
    } else {
      newMap[dayIdx] = next;
    }
    const serialized = dayMapToWorkingHours(newMap);
    onChange(serialized.length === 0 ? null : serialized);
  };

  const noDaysActive = Object.keys(dayMap).length === 0;

  return (
    <Stack spacing={0.75}>
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'flex-start',
          px: 1.5,
          py: 1.25,
          borderRadius: RADIUS_ITEM,
          bgcolor: noDaysActive ? alpha(YELLOW, 0.18) : alpha(NAVY, 0.04),
          transition: 'background-color 0.2s ease',
        }}
      >
        <Typography sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.8125rem', color: NAVY, lineHeight: 1.5 }}>
          {noDaysActive ? (
            <>
              <strong>Disponible 24/7.</strong> Sin días activados, este usuario puede recibir asignaciones en cualquier
              momento. Activa al menos un día abajo para limitar su horario.
            </>
          ) : (
            <>
              <strong>Horario limitado.</strong> Solo recibirá asignaciones automáticas en los días y horas activadas.
              Desactiva todos los días para volver a 24/7.
            </>
          )}
        </Typography>
      </Box>
      {WEEK_DAYS.map(({ idx, label }) => {
        const entry = dayMap[idx];
        const enabled = !!entry;
        const start = entry?.start ?? 9;
        const end = entry?.end ?? 18;

        return (
          <Box
            key={idx}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 0.5,
              px: 1.25,
              borderRadius: RADIUS_ITEM,
              '&:hover': { bgcolor: alpha(NAVY, 0.03) },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 110 }}>
              <Switch
                checked={enabled}
                onChange={(e) => updateDay(idx, e.target.checked ? { start, end } : null)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: NAVY },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: YELLOW, opacity: 1 },
                }}
              />
              <Typography
                sx={{ fontFamily: 'Jost, sans-serif', fontSize: '0.875rem', fontWeight: 500, color: NAVY, ml: 0.5 }}
              >
                {label}
              </Typography>
            </Box>
            <FormControl sx={{ minWidth: 100 }} disabled={!enabled}>
              <Select
                value={start}
                onChange={(e) => updateDay(idx, { start: Number(e.target.value), end })}
                sx={selectSx}
              >
                {START_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h} sx={{ fontFamily: 'Jost, sans-serif' }}>
                    {formatHour(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography sx={{ color: alpha(NAVY, 0.45), fontSize: '0.875rem' }}>a</Typography>
            <FormControl sx={{ minWidth: 100 }} disabled={!enabled}>
              <Select
                value={end}
                onChange={(e) => updateDay(idx, { start, end: Number(e.target.value) })}
                sx={selectSx}
              >
                {END_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h} sx={{ fontFamily: 'Jost, sans-serif' }}>
                    {formatHour(h)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {enabled && end <= start && (
              <Typography sx={{ color: '#E7233B', fontSize: '0.75rem', fontFamily: 'Jost, sans-serif' }}>
                Hora fin debe ser mayor
              </Typography>
            )}
          </Box>
        );
      })}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mt: 0.5, px: 1.25 }}>
        {value && value.length > 0 && (
          <Box
            component="button"
            type="button"
            onClick={() => onChange(null)}
            sx={{
              border: 'none',
              background: 'transparent',
              color: NAVY,
              fontFamily: 'Jost, sans-serif',
              fontSize: '0.75rem',
              fontWeight: 600,
              cursor: 'pointer',
              p: 0.5,
              borderRadius: RADIUS_ITEM,
              '&:hover': { bgcolor: alpha(NAVY, 0.06) },
            }}
          >
            Limpiar todo
          </Box>
        )}
      </Box>
    </Stack>
  );
};

export default WorkingHoursEditor;
